/**
 * Content Library — transfer (export / import) HTTP API wrappers.
 *
 * Export posts the ids currently visible in the gallery and downloads the
 * resulting bundle zip. Import is two-step: upload for a dry-run preview, then
 * commit the staged bundle (super-admin only, enforced server-side).
 *
 * Follows the same conventions as ./trailblazers/trlImageApi.ts — raw fetch with
 * `credentials: 'include'` so the httpOnly auth cookie is sent.
 */

import { API_URL } from '../../config';
import type { ContentImportPreview, ContentImportResult } from '@swtorbit/shared-socket-events';

/** Base URL for the Content Library transfer module. */
const BASE = `${API_URL}/api/content-library/transfer`;

/** Read the `{ error }` body from a non-2xx response and throw. */
async function throwApiError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({ error: fallback }));
  throw new Error(data.error || fallback);
}

/**
 * Filename from a `Content-Disposition` header, or a fallback.
 * The server names the bundle (it owns the timestamp), so honour it.
 */
function filenameFromDisposition(header: string | null, fallback: string): string {
  const match = header?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

/**
 * Export the given assets as a bundle and trigger a browser download.
 *
 * The whole zip is buffered into a blob before saving, because a download
 * initiated from `fetch` cannot stream to disk. That is acceptable here — the
 * operator chose this set, and the alternative (a plain link) cannot send the
 * id list or the auth cookie in one request.
 *
 * @endpoint POST /api/content-library/transfer/export
 */
export async function exportTrlBundle(ids: string[]): Promise<void> {
  return downloadBundle({ ids });
}

/**
 * Export the *entire* library — every screenshot, including other admins'
 * private ones. Super-admin only (enforced server-side).
 *
 * Sends no id list: private screenshots owned by other admins never appear in
 * any gallery, so the client cannot enumerate them and the server selects the
 * set itself.
 *
 * @endpoint POST /api/content-library/transfer/export
 */
export async function exportEntireTrlLibrary(): Promise<void> {
  return downloadBundle({ all: true });
}

/** POST an export request and save the streamed zip it returns. */
async function downloadBundle(body: { ids: string[] } | { all: true }): Promise<void> {
  const response = await fetch(`${BASE}/export`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwApiError(response, 'Failed to export bundle');

  const blob = await response.blob();
  const filename = filenameFromDisposition(
    response.headers.get('Content-Disposition'),
    'trl-library-export.zip',
  );

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoking immediately can cancel the download in some browsers; give the
    // click a tick to be picked up first.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/**
 * Upload a bundle and get its dry-run preview. Nothing is written to the
 * database — the returned `stagedId` is passed to {@link commitTrlImport}.
 *
 * Uses XMLHttpRequest rather than fetch: bundles run to hundreds of MB and
 * `fetch` still cannot report upload progress, so there would be no way to show
 * the operator that a long upload is alive.
 *
 * @endpoint POST /api/content-library/transfer/import/preview
 */
export function previewTrlImport(
  bundle: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ContentImportPreview> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('bundle', bundle);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/import/preview`);
    xhr.withCredentials = true;
    xhr.responseType = 'json';

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    });

    xhr.addEventListener('load', () => {
      // `responseType: 'json'` leaves `response` null when the body is not JSON
      // (e.g. a proxy's HTML error page), so fall back to a status message.
      const body = xhr.response as { preview?: ContentImportPreview; error?: string } | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.preview) {
        resolve(body.preview);
      } else {
        reject(new Error(body?.error || `Failed to read bundle (HTTP ${xhr.status})`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Network error while uploading the bundle')));
    xhr.addEventListener('abort', () => reject(new DOMException('Upload cancelled', 'AbortError')));

    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(form);
  });
}

/**
 * Apply a staged bundle.
 * @endpoint POST /api/content-library/transfer/import/commit
 */
export async function commitTrlImport(
  stagedId: string,
  options: { shareAll: boolean },
): Promise<ContentImportResult> {
  const response = await fetch(`${BASE}/import/commit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stagedId, shareAll: options.shareAll }),
  });
  if (!response.ok) await throwApiError(response, 'Failed to import bundle');
  const data = await response.json();
  return data.result as ContentImportResult;
}

/**
 * Discard a staged bundle the operator decided not to import.
 * Best-effort: a failure here only leaves a directory for the server to sweep.
 * @endpoint DELETE /api/content-library/transfer/import/:stagedId
 */
export async function discardTrlImport(stagedId: string): Promise<void> {
  await fetch(`${BASE}/import/${encodeURIComponent(stagedId)}`, {
    method: 'DELETE',
    credentials: 'include',
  }).catch(() => { /* best-effort */ });
}
